"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import Api from "../../config/Api";
import { toast } from "react-toastify";
import Loader from "../Widget/Loader";
import Table from "../Widget/Table";
import TooltipWidget from "../Widget/TooltipWidget";
import dayjs from "dayjs";
import { BsFillEyeFill } from "react-icons/bs";
import ModalBox from "../Widget/ModalBox";
import ReactToPrint from "react-to-print";
import { FaPrint } from "react-icons/fa";
import logo from "../../assets/images/logo.png";

const statusVariant = (status) => {
  const s = String(status).toLowerCase();
  if (s === "success" || s === "released") return "success";
  if (s === "failed" || s === "failed payment") return "danger";
  if (s === "processing" || s === "under process") return "warning";
  if (s === "reversed") return "secondary";
  return "info";
};

// Older payouts have no application_no; fall back to the linked application's number.
const appNo = (row) => row?.application_no || row?.application?.application_id || "-";

const formatDate = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "-");

const PaymentHistory = () => {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [totalRows, setTotalRows]     = useState(0);
  const [perPage, setPerPage]         = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch]           = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [districtList, setDistrictList] = useState([]);
  const [districtId, setDistrictId]     = useState("");
  const [startDate, setStartDate]       = useState("");
  const [endDate, setEndDate]           = useState("");

  const [modalShow, setModalShow]       = useState(false);
  const [modalData, setModalData]       = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const componentRef = useRef();

  const fetchData = useCallback(async (page, rows, q, district, from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: rows });
      if (q) params.append("search", q);
      if (district) params.append("district_id", district);
      if (from) params.append("start_date", from);
      if (to) params.append("end_date", to);
      const res = await Api(`payment_history?${params.toString()}`);
      if (res.data.status === 1) {
        setData(res.data.data);
        setTotalRows(res.data.total);
        // District dropdown is scoped by the server: admins get all, others only theirs.
        setDistrictList(res.data.allowed_districts || []);
      } else {
        toast.error(res.data.message, { position: toast.POSITION.BOTTOM_RIGHT });
      }
    } catch {
      toast.error("Something went wrong!", { position: toast.POSITION.BOTTOM_RIGHT });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, perPage, search, districtId, startDate, endDate);
  }, [currentPage, perPage, search, districtId, startDate, endDate, fetchData]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleDistrictChange = (e) => {
    setDistrictId(e.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const hasActiveFilters = search || districtId || startDate || endDate;

  const clearSearch = () => {
    setSearch("");
    setSearchInput("");
    setDistrictId("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const openDetail = async (row) => {
    setModalShow(true);
    setModalLoading(true);
    setModalData(null);
    try {
      const res = await Api(`payment_history/${row.id}`);
      if (res.data.status === 1) {
        setModalData(res.data.data);
      } else {
        toast.error("Failed to load details", { position: toast.POSITION.BOTTOM_RIGHT });
      }
    } catch {
      toast.error("Something went wrong!", { position: toast.POSITION.BOTTOM_RIGHT });
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalShow(false);
    setModalData(null);
  };

  const columns = [
    {
      name: "#",
      selector: (row) => row._sr,
      width: "60px",
    },
    {
      name: "Application Number",
      selector: (row) => appNo(row),
      sortable: true,
      wrap: true,
      minWidth: "150px",
    },
    {
      name: "Payment Type",
      selector: (row) => row.application?.payment_mode || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Amount",
      selector: (row) => `₹${Number(row.amount).toLocaleString("en-IN")}`,
      sortable: true,
      sortFunction: (a, b) => Number(a.amount) - Number(b.amount),
      wrap: true,
    },
    {
      name: "Reference ID",
      selector: (row) => row.reference_id || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Sanction Date",
      selector: (row) => formatDate(row.application?.sanction_date),
      sortable: true,
      sortFunction: (a, b) =>
        String(a.application?.sanction_date || "").localeCompare(String(b.application?.sanction_date || "")),
      wrap: true,
      minWidth: "130px",
    },
    {
      name: "Status",
      selector: (row) =>
        row.status ? (
          <span className={`badge bg-${statusVariant(row.status)}`}>{row.status}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Date",
      selector: (row) => dayjs(row.created_at).format("DD MMM YYYY hh:mm A"),
      sortable: true,
      wrap: true,
      minWidth: "170px",
    },
    {
      name: "Action",
      selector: (row) => (
        <TooltipWidget title="View Details">
          <button className="btn btn-warning btn-sm" onClick={() => openDetail(row)}>
            <BsFillEyeFill />
          </button>
        </TooltipWidget>
      ),
      width: "80px",
    },
  ];

  const dataWithSr = data.map((row, i) => ({
    ...row,
    _sr: (currentPage - 1) * perPage + i + 1,
  }));

  return (
    <>
      <div className="bg-white p-3 rounded shadow">
        {/* Header + filters */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h5 className="text-dark mb-0 fw-semibold">Payment History</h5>
          <form onSubmit={handleSearch} className="d-flex gap-2 align-items-center flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: "160px" }}
              aria-label="District"
              value={districtId}
              onChange={handleDistrictChange}
            >
              <option value="">All Districts</option>
              {districtList
                ?.filter((district) => district.status === "Active")
                .map((district) => (
                  <option value={district?.id} key={district?.id}>
                    {district?.district_name}
                  </option>
                ))}
            </select>
            <div className="d-flex align-items-center gap-1">
              <span className="small text-muted text-nowrap">Sanction Date</span>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: "150px" }}
                title="Sanction Date From"
                aria-label="Sanction Date From"
                value={startDate}
                max={endDate || undefined}
                onChange={handleStartDateChange}
              />
              <span className="small text-muted">to</span>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: "150px" }}
                title="Sanction Date To"
                aria-label="Sanction Date To"
                value={endDate}
                min={startDate || undefined}
                onChange={handleEndDateChange}
              />
            </div>
            <div className="input-group input-group-sm" style={{ width: "360px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Application Number / Ref ID / Name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button type="submit" className="btn btn-primary px-3">
                Search
              </button>
            </div>
            {hasActiveFilters && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearSearch}>
                Clear
              </button>
            )}
          </form>
        </div>

        <Table
          columns={columns}
          data={dataWithSr}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          noDataComponent={<div className="py-4 text-muted text-center">No payment records found.</div>}
        />
      </div>

      {/* Hidden print template */}
      <div ref={componentRef} style={{ padding: "20px", margin: "40px 10px", display: "none" }} className="print-section">
        {modalData && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th colSpan={2} style={{ textAlign: "center", padding: "10px" }}>
                  <img src={logo} style={{ height: "120px", marginBottom: "30px" }} alt="logo" />
                </th>
              </tr>
              <tr>
                <th colSpan={2} style={{ textAlign: "center", padding: "10px", color: "#252372", fontSize: "25px", fontWeight: "500" }}>
                  Application Payment Report (APR)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "10px 10px 0 10px" }} colSpan={2}>
                  <h5 style={{ margin: 0, color: "#000000de", fontWeight: "500" }}>
                    Application Number: {appNo(modalData)}
                  </h5>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px", verticalAlign: "top" }}>
                  <p style={{ margin: "2px 0", fontSize: "18px" }}>{modalData.account_holder_name}</p>
                </td>
                <td style={{ padding: "10px", textAlign: "end" }}>
                  <p>{dayjs(modalData.created_at).format("DD MMM YYYY hh:mm A")}</p>
                  <p>{modalData.contact}</p>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "5px 10px" }}>
                  <p>Account Number: {modalData.account_no}</p>
                  <p>IFSC: {modalData.ifsc}</p>
                  <p>Payment Type: {modalData.application?.payment_mode || "-"}</p>
                  <p>Sanction Date: {formatDate(modalData.application?.sanction_date)}</p>
                </td>
                <td style={{ textAlign: "end", padding: "5px 10px" }}>
                  <h3 style={{ color: "#252372" }}>₹{Number(modalData.amount).toLocaleString("en-IN")}</h3>
                  <h6 style={{ color: "#387123", fontSize: "18px" }}>{modalData.status}</h6>
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #d8d8ff", padding: "10px" }}>Reference ID</td>
                <td style={{ border: "1px solid #d8d8ff", padding: "10px" }}>{modalData.reference_id}</td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #d8d8ff", padding: "10px" }}>Payment Type Other</td>
                <td style={{ border: "1px solid #d8d8ff", padding: "10px" }}>{modalData.application?.payment_type_other || "-"}</td>
              </tr>
              <tr style={{ height: "30px" }} />
              <tr>
                <td style={{ textAlign: "center", border: "1px solid #d8d8ff", padding: "10px" }}>
                  <h6 style={{ color: "#252372" }}>Uploaded Photo</h6>
                  {modalData.application?.applicant_default_img && (
                    <img src={modalData.application.applicant_default_img} alt="Uploaded" style={{ maxWidth: "100px", height: "auto" }} />
                  )}
                </td>
                <td style={{ textAlign: "center", border: "1px solid #d8d8ff", padding: "10px" }}>
                  <h6 style={{ color: "#252372" }}>Captured Photo</h6>
                  {modalData.application?.applicant_img && (
                    <img src={modalData.application.applicant_img} alt="Captured" style={{ maxWidth: "100px", height: "auto" }} />
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={{ textAlign: "center", padding: "20px 10px 0 10px", color: "#888", fontSize: "12px" }}>
                  This is a digitally generated APR. Signature is not required.
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      <ModalBox show={modalShow} hide={closeModal} title="Payment Detail" size="lg">
        {modalLoading ? (
          <div className="text-center py-5">
            <Loader />
          </div>
        ) : modalData ? (
          <>
            <ReactToPrint
              trigger={() => (
                <div className="text-end mb-3">
                  <button type="button" className="btn btn-success btn-sm px-2">
                    <FaPrint /> Print
                  </button>
                </div>
              )}
              content={() => componentRef.current}
            />
            <div className="p-2">
              {/* Photos */}
              {(modalData.application?.applicant_default_img || modalData.application?.applicant_img) && (
                <div className="row py-2 mb-3 mx-0" style={{ border: "1px solid #ddd", borderRadius: "6px" }}>
                  <div className="col-6 text-center">
                    <p className="small fw-bold mb-1" style={{ color: "#252372" }}>Uploaded Photo</p>
                    <img
                      src={modalData.application.applicant_default_img}
                      alt="Uploaded"
                      style={{ maxWidth: "100px", height: "auto", borderRadius: "4px" }}
                    />
                  </div>
                  <div className="col-6 text-center">
                    <p className="small fw-bold mb-1" style={{ color: "#252372" }}>Captured Photo</p>
                    <img
                      src={modalData.application.applicant_img}
                      alt="Captured"
                      style={{ maxWidth: "100px", height: "auto", borderRadius: "4px" }}
                    />
                  </div>
                </div>
              )}

              {/* Details grid */}
              <div className="row g-2">
                {[
                  ["Application Number", appNo(modalData)],
                  ["Name", modalData.account_holder_name],
                  ["Contact Number", modalData.contact],
                  ["Amount", `₹${Number(modalData.amount).toLocaleString("en-IN")}`],
                  ["Account Number", modalData.account_no],
                  ["IFSC", modalData.ifsc],
                  ["Payment Type", modalData.application?.payment_mode || "-"],
                  ["Payment Type Other", modalData.application?.payment_type_other || "-"],
                  ["Sanction Date", formatDate(modalData.application?.sanction_date)],
                  ["Reference ID", modalData.reference_id],
                  ["Status", modalData.status],
                  ["Date", dayjs(modalData.created_at).format("DD MMM YYYY hh:mm A")],
                ].map(([label, value], i) => (
                  <div key={i} className="col-6">
                    <div className="px-3 py-2 rounded paylistst" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
                      <label className="small fw-bold text-muted mb-0">{label}</label>
                      <p className="mb-0 mt-1" style={{ fontSize: "0.9rem" }}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-muted small mt-3 mb-0">
                This is a digitally generated APR. Signature is not required.
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-muted">Failed to load details.</div>
        )}
      </ModalBox>
    </>
  );
};

export default PaymentHistory;
